import { doc, getDoc, setDoc } from "firebase/firestore";
import { db, geminiModel } from "../firebase";

/**
 * Service for generating and managing narrative content using Gemini
 */
class NarrativeService {
  constructor() {
    // Use the pre-initialized Gemini model from firebase.js
    this.model = geminiModel;
    // Keep a local in-memory cache to prevent redundant calls in the same session
    this.localCache = {};
    // Track pending requests to avoid duplicate API calls
    this.pendingRequests = {};
  }

  /**
   * Generate a narrative for a specific section of a brand profile
   * Always prioritizes using cached narratives from Firestore
   * Never regenerates manually edited narratives
   *
   * @param {string} responseId - The ID of the questionnaire response or brand profile
   * @param {string} section - The section name (e.g., "characteristics", "demographics")
   * @param {object} sectionData - The data for the section
   * @param {string} companyUrl - Optional URL of the company website for additional context
   * @param {boolean} forceRegenerate - Whether to force regeneration even if a cached version exists
   * @returns {Promise<string>} - The generated narrative
   */
  async generateNarrative(
    responseId,
    section,
    sectionData,
    companyUrl = null,
    forceRegenerate = false
  ) {
    const cacheKey = `${responseId}_${section}`;

    // Early exit if sectionData is empty or contains only empty values
    if (
      !sectionData ||
      (typeof sectionData === "object" &&
        Object.keys(sectionData).length === 0) ||
      (typeof sectionData === "object" &&
        Object.values(sectionData).every(
          (v) =>
            v === null ||
            v === undefined ||
            v === "" ||
            (Array.isArray(v) && v.length === 0) ||
            (typeof v === "object" && Object.keys(v).length === 0)
        ))
    ) {
      return "No data available for this section.";
    }

    // If a request is already in progress for this key, return its promise
    if (!forceRegenerate && this.pendingRequests[cacheKey]) {
      return this.pendingRequests[cacheKey];
    }

    // Create a promise for this request and store it
    const requestPromise = (async () => {
      try {
        // First check local cache
        if (!forceRegenerate && this.localCache[cacheKey]) {
          return this.localCache[cacheKey];
        }

        // Only treat as legacy if the document exists in the brandProfiles collection
        const brandProfileRef = doc(db, "brandProfiles", responseId);
        let profileSnap;
        try {
          profileSnap = await getDoc(brandProfileRef);
        } catch (e) {
          // Error handled silently
        }
        if (profileSnap && profileSnap.exists() && !forceRegenerate) {
          const profileData = profileSnap.data();

          // Check if narrative exists in the legacy format
          if (
            profileData &&
            profileData.narratives &&
            profileData.narratives[section]
          ) {
            // Handle both formats: string or object with text property
            const legacyNarrative = profileData.narratives[section];
            let narrativeText = "";

            // Check if the narrative is a string or an object
            if (typeof legacyNarrative === "string") {
              narrativeText = legacyNarrative;
            } else if (typeof legacyNarrative === "object") {
              // If it's an object, try to get the text property
              narrativeText =
                legacyNarrative.text || JSON.stringify(legacyNarrative);
            } else {
              // Fallback for any other type
              narrativeText = String(legacyNarrative);
            }

            // Add to local cache
            this.localCache[cacheKey] = narrativeText;
            return narrativeText;
          }

          // If no narrative exists for this section in a legacy profile, return a placeholder
          const placeholderText =
            "Legacy profile narrative not available. Please contact support if you need this regenerated.";
          this.localCache[cacheKey] = placeholderText;
          return placeholderText;
        }

        // Then check if we have a cached version in Firestore for new questionnaire responses
        if (!forceRegenerate) {
          const responseRef = doc(db, "questionnaireResponses", responseId);
          let responseSnap;
          try {
            responseSnap = await getDoc(responseRef);
          } catch (e) {
            // Error handled silently
          }

          // Get company information for context
          let companyName = "the brand";
          let companyInfo = {};

          if (responseSnap && responseSnap.exists()) {
            const responseData = responseSnap.data();

            // Extract company name and info for context
            if (responseData.data) {
              // Attempt to find brand info in the response data
              const brandInfoSection = Object.values(responseData.data).find(
                (section) => section.sectionTitle === "Brand Information"
              );

              if (brandInfoSection && brandInfoSection.responses) {
                const brandNameResponse = Object.values(
                  brandInfoSection.responses
                ).find((q) => q.questionText === "Brand Name");

                if (brandNameResponse && brandNameResponse.value) {
                  companyName = brandNameResponse.value;
                }

                // Try to get other company info
                const industryResponse = Object.values(
                  brandInfoSection.responses
                ).find((q) =>
                  q.questionText.toLowerCase().includes("industry")
                );

                companyInfo = {
                  industry: industryResponse?.value || "",
                  companySize: "",
                  location: "",
                  founded: "",
                };
              }
            }

            // If narratives exist and this section has a narrative, return it immediately (unless forcing regeneration)
            if (
              responseData.narratives &&
              responseData.narratives[section] &&
              responseData.narratives[section].text
            ) {
              const cachedNarrative = responseData.narratives[section];

              // Always use the existing narrative if it's been manually edited
              if (cachedNarrative.manuallyEdited) {
                // Store in local cache
                this.localCache[cacheKey] = cachedNarrative.text;
                return cachedNarrative.text;
              }

              // Otherwise, use the cached AI-generated narrative
              // Store in local cache
              this.localCache[cacheKey] = cachedNarrative.text;
              return cachedNarrative.text;
            }
          }
        }

        // If forcing regeneration or no narrative exists in Firestore for new questionnaire responses, generate a new one
        // Get company information for the prompt from the response if possible
        let companyName = "the brand";
        let companyInfo = {};

        // Try to get company info from firestore for better prompts
        try {
          const responseRef = doc(db, "questionnaireResponses", responseId);
          const responseSnap = await getDoc(responseRef);
          if (responseSnap.exists()) {
            const responseData = responseSnap.data();

            // Attempt to find brand info in the response data
            if (responseData.data) {
              const brandInfoSection = Object.values(responseData.data).find(
                (section) => section.sectionTitle === "Brand Information"
              );

              if (brandInfoSection && brandInfoSection.responses) {
                const brandNameResponse = Object.values(
                  brandInfoSection.responses
                ).find((q) => q.questionText === "Brand Name");

                if (brandNameResponse && brandNameResponse.value) {
                  companyName = brandNameResponse.value;
                }

                // Try to get other company info
                const industryResponse = Object.values(
                  brandInfoSection.responses
                ).find((q) =>
                  q.questionText.toLowerCase().includes("industry")
                );

                companyInfo = {
                  industry: industryResponse?.value || "",
                  companySize: "",
                  location: "",
                  founded: "",
                };
              }
            }
          }
        } catch (e) {
          // Continue with default values if company info can't be retrieved
        }

        const prompt = this.createPromptForSection(
          section,
          sectionData,
          companyName,
          companyInfo,
          companyUrl
        );

        // Generate content using Gemini
        let result, narrative;
        try {
          result = await this.model.generateContent(prompt);
          narrative = result.response.text();
        } catch (e) {
          throw e;
        }

        // Store data hash for future reference
        const dataHash = this.generateDataHash(sectionData);

        // Update the response with the new narrative
        try {
          await this.saveNarrativeToResponse(
            responseId,
            section,
            narrative,
            dataHash
          );
        } catch (e) {
          // Error handled silently
        }

        // Add to local cache
        this.localCache[cacheKey] = narrative;
        return narrative;
      } catch (error) {
        // Return a fallback message if generation fails
        return "Unable to generate narrative at this time. Please try again later.";
      } finally {
        // Clean up the pending request after it finishes
        delete this.pendingRequests[cacheKey];
      }
    })();

    this.pendingRequests[cacheKey] = requestPromise;
    return requestPromise;
  }

  /**
   * Generate a consistent hash for section data to compare for changes
   *
   * @param {object} sectionData - The data for the section
   * @returns {string} - A hash of the data
   */
  generateDataHash(sectionData) {
    // Sort keys to ensure consistent hashing regardless of property order
    if (!sectionData) return "";

    const normalized = JSON.stringify(
      sectionData,
      Object.keys(sectionData).sort()
    );
    return normalized;
  }

  /**
   * Save a generated narrative to the questionnaire response
   *
   * @param {string} responseId - The ID of the questionnaire response
   * @param {string} section - The section name
   * @param {string} narrative - The generated narrative text
   * @param {string} dataHash - The hash of the data used to generate the narrative
   */
  async saveNarrativeToResponse(responseId, section, narrative, dataHash) {
    try {
      // Check if this is a legacy profile (exists in brandProfiles)
      const brandProfileRef = doc(db, "brandProfiles", responseId);
      const brandProfileSnap = await getDoc(brandProfileRef);
      if (brandProfileSnap.exists()) {
        return;
      }

      const responseRef = doc(db, "questionnaireResponses", responseId);
      const responseSnap = await getDoc(responseRef);

      if (responseSnap.exists()) {
        const responseData = responseSnap.data();
        const narratives = responseData.narratives || {};

        // Make sure we don't overwrite a manually edited narrative
        if (narratives[section] && narratives[section].manuallyEdited) {
          return;
        }

        // Update the narratives object with the new narrative
        narratives[section] = {
          text: narrative,
          dataHash: dataHash,
          generatedAt: new Date(),
          manuallyEdited: false,
        };

        // Update the response document
        await setDoc(responseRef, { narratives }, { merge: true });
      }
    } catch (error) {
      // Error handled silently
    }
  }

  /**
   * Fetch all narratives for a given questionnaire response
   * @param {string} responseId
   * @returns {Promise<object>} - The narratives object or empty object
   */
  async getAllNarratives(responseId) {
    try {
      const responseRef = doc(db, "questionnaireResponses", responseId);
      const responseSnap = await getDoc(responseRef);
      if (responseSnap.exists()) {
        const responseData = responseSnap.data();
        return responseData.narratives || {};
      }
      return {};
    } catch (error) {
      return {};
    }
  }

  /**
   * Create appropriate prompt for different sections
   *
   * @param {string} section - The section name
   * @param {object} sectionData - The data for the section
   * @param {string} companyName - The name of the company
   * @param {object} companyInfo - Additional company information
   * @param {string} companyUrl - Optional URL of the company website
   * @returns {string} - The prompt for Gemini
   */
  createPromptForSection(
    section,
    sectionData,
    companyName,
    companyInfo,
    companyUrl
  ) {
    const companyContext = `
        Company Name: ${companyName}
        Industry: ${companyInfo.industry || "Not specified"}
        Company Size: ${companyInfo.companySize || "Not specified"}
        Location: ${companyInfo.location || "Not specified"}
        Founded: ${companyInfo.founded || "Not specified"}
        Website: ${companyUrl || "Not provided"}
        `;

    const basePrompt = `You are a professional brand strategist writing a narrative for ${companyName}'s brand profile. 
        Write a concise, insightful analysis about ${companyName}'s ${section} based on the following data. Limit your response to a maximum of 3 sentences.
        
        IMPORTANT GUIDELINES:
        - Use a professional, business-focused tone
        - Avoid flowery language and excessive adjectives
        - Always refer to the brand by its actual name "${companyName}" (never use placeholders like "[Brand Name]")
        - Focus on concrete analysis rather than poetic descriptions
        - Be specific and data-driven in your insights
        - Keep the analysis practical and actionable for business purposes
        - Do not exceed 3 sentences in your response
        
        Company Context:
        ${companyContext}
        
        Data for ${section}:
        ${JSON.stringify(sectionData, null, 2)}
        `;

    // Add section-specific instructions
    switch (section) {
      case "characteristics":
        return (
          basePrompt +
          `
                  Focus on the most distinctive characteristics (values at extremes of 1-2 or 4-5).
                  Explain what these characteristics reveal about ${companyName}'s brand personality.
                  Suggest how these traits might influence customer perception and brand experience.
                  Analyze how these characteristics could be expressed through sound (tempo, pitch, timbre, rhythm, etc.).
                  Consider how characteristics like energy, formality, and playfulness translate to sonic elements.
                  Provide practical implications for ${companyName}'s marketing, communication strategy, and sonic branding.
                `
        );

      case "demographics":
        return (
          basePrompt +
          `
                  Compare ${companyName}'s current and target demographics.
                  Identify specific opportunities for growth or repositioning.
                  Consider how demographic factors might influence music and sound preferences.
                  Analyze how age, gender, territory, and lifestyle could inform sonic branding decisions.
                  Suggest practical ways ${companyName} might better connect with its target audience through sound.
                  Focus on actionable insights for creating audio experiences that resonate with the target demographic.
                `
        );

      case "brandStyle":
        return (
          basePrompt +
          `
                  Analyze how ${companyName}'s visual elements could translate to a sonic identity.
                  Explain the psychological impact of the brand's color choices and how they might correspond to sonic elements (tempo, pitch, timbre).
                  Suggest how the visual identity could be complemented by sound to create a cohesive multi-sensory brand experience.
                  Consider how the brand's fonts, colors, and imagery might sound if translated to music or sound design.
                  Provide practical recommendations for creating sonic elements that align with the visual brand identity.
                  Discuss how the visual style could inform decisions about musical genres, instruments, and sound textures.
                `
        );

      case "brandStrategy":
        return (
          basePrompt +
          `
                  Evaluate the coherence of ${companyName}'s strategic positioning.
                  Identify specific strengths and potential areas for improvement.
                  Suggest how the strategy aligns with current market trends and customer needs.
                  Consider how sonic branding could enhance ${companyName}'s competitive differentiation.
                  Analyze how sound strategy could complement the overall brand strategy.
                  Recommend how ${companyName} could use sound strategically to strengthen brand recognition and recall.
                  Provide actionable recommendations for integrating sonic elements into ${companyName}'s brand strategy.
                `
        );

      case "brandSound":
        return (
          basePrompt +
          `
                  Describe how ${companyName}'s sound elements contribute to its brand identity.
                  Explain how sound reinforces ${companyName}'s personality traits and complements its visual identity.
                  Analyze how the current or proposed sonic elements align with the brand's visual style.
                  Suggest specific opportunities for developing a cohesive audio-visual brand experience.
                  Recommend how ${companyName} can create a distinctive sonic identity that stands out in its industry.
                  Provide practical recommendations for audio consistency across channels that complement the visual brand.
                `
        );

      case "projectGoals":
        return (
          basePrompt +
          `
                  Analyze how ${companyName}'s project goals align with its overall strategy.
                  Identify key success metrics and potential challenges.
                  Consider how sonic branding goals could be integrated into the project objectives.
                  Suggest a practical roadmap for achieving these goals effectively, including sonic milestones.
                  Recommend how to measure the impact and effectiveness of sonic branding elements.
                  Provide specific recommendations for measuring progress and success of audio-visual brand integration.
                `
        );

      case "brandBackground":
        return (
          basePrompt +
          `
                  Analyze ${companyName}'s history and evolution in a business context.
                  Identify key milestones and turning points in the brand's development.
                  Consider any historical sound associations or audio heritage the brand might have.
                  Suggest how ${companyName}'s background influences its current market positioning.
                  Analyze how the brand's history could inform authentic sonic branding decisions.
                  Provide insights on how the brand history can be leveraged for future growth, including sonic identity.
                `
        );

      default:
        return (
          basePrompt +
          `
                  Provide a comprehensive analysis of this aspect of ${companyName}'s brand.
                  Identify key strengths and opportunities for development.
                  Consider how this element relates to ${companyName}'s sonic branding potential.
                  Suggest how this element connects to ${companyName}'s overall brand strategy.
                  Analyze how this aspect could be expressed or complemented through sound.
                  Provide practical, business-focused recommendations for multi-sensory brand integration.
                `
        );
    }
  }

  /**
   * Optimize a question-response pair for visual display
   * Transforms both the question title and the response content into more polished versions
   *
   * @param {string} questionText - The original question text
   * @param {string|array} responseValue - The response value (could be text, array of options, etc.)
   * @param {string} brandName - The name of the brand
   * @param {string} sectionName - The section this question belongs to
   * @returns {Promise<object>} - Object containing optimized title and content
   */
  async optimizeQuestionResponse(
    questionText,
    responseValue,
    brandName = "the brand",
    sectionName = ""
  ) {
    try {
      const safeBrandName = brandName || "the brand";
      const prompt = `
        You are a brand strategist. Rewrite the following question and answer for display in a brand profile.
        - Make the question title concise and professional.
        - Make the answer clear, engaging, and suitable for a brand profile.
        - Brand name: "${safeBrandName}"
        - Section: "${sectionName}"
        Question: "${
          typeof responseValue === "string"
            ? responseValue
            : JSON.stringify(responseValue)
        }"
        Respond in JSON with keys: displayTitle, displayContent.
      `;

      const result = await this.model.generateContent(prompt);
      const text = result.response.text();

      let textToParse = text.trim();
      // Remove code block markers if present
      if (textToParse.startsWith("```json")) {
        textToParse = textToParse
          .replace(/^```json/, "")
          .replace(/```$/, "")
          .trim();
      } else if (textToParse.startsWith("```")) {
        textToParse = textToParse
          .replace(/^```/, "")
          .replace(/```$/, "")
          .trim();
      }

      let optimized;
      try {
        optimized = JSON.parse(textToParse);
        // If displayContent is itself a JSON string, parse it again
        if (
          typeof optimized.displayContent === "string" &&
          optimized.displayContent.trim().startsWith("{")
        ) {
          try {
            const inner = JSON.parse(optimized.displayContent);
            if (inner.displayTitle && inner.displayContent) {
              optimized = inner;
            }
          } catch (e) {
            // ignore, use as is
          }
        }
      } catch (e) {
        // Fallback: return as plain text
        optimized = {
          displayTitle: questionText,
          displayContent: text,
        };
      }
      return optimized;
    } catch (error) {
      return null;
    }
  }
}

export { NarrativeService };
const narrativeService = new NarrativeService();
export default narrativeService;
